import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
    Code,
    Brain,
    //Settings,
    //Rocket,
    //Zap,
    //ShieldCheck,
    //Terminal,
    //GitBranch,
    GitBranch,
    //Cloud,
    Lightbulb,
    Wand2,
    MousePointerClick,
} from 'lucide-react'
import { Menu, X } from 'lucide-react'
import logo from './assets/logo.png'

const features = [
    {
        icon: <Code size={28} />,
        title: 'AI-Powered Autocomplete',
        description: 'Lightning-fast, context-aware suggestions as you type.',
    },
    {
        icon: <Brain size={28} />,
        title: 'Instant Code Explanation',
        description: 'Understand any block of code with one click.',
    },
    {
        icon: <GitBranch size={28} />,
        title: 'Architecture Diagrams',
        description:
            'Visualize your codebase structure with AI-generated diagrams.',
    },
    // {
    //     icon: <Settings size={28} />,
    //     title: 'Customizable Workflows',
    //     description: 'Set up your dev environment exactly the way you want it.',
    // },
    // {
    //     icon: <Rocket size={28} />,
    //     title: 'One-Click Deployment',
    //     description: 'Deploy your project to the cloud instantly.',
    // },
    // {
    //     icon: <Zap size={28} />,
    //     title: 'Real-Time Collaboration',
    //     description: 'Work together with teammates in real-time.',
    // },
    // {
    //     icon: <ShieldCheck size={28} />,
    //     title: 'Smart Error Detection',
    //     description: 'AI detects bugs and suggests fixes on the fly.',
    // },
    // {
    //     icon: <Terminal size={28} />,
    //     title: 'Integrated Terminal',
    //     description: 'Built-in terminal with intelligent command suggestions.',
    // },
    // {
    //     icon: <GitBranch size={28} />,
    //     title: 'Git Integration',
    //     description:
    //         'Seamless git management with AI-assisted commit messages.',
    // },
    // {
    //     icon: <Cloud size={28} />,
    //     title: 'Cloud Sync',
    //     description: 'Your code, everywhere, always in sync.',
    // },
    {
        icon: <Lightbulb size={28} />,
        title: 'AI Refactor',
        description: 'Automatically refactor legacy codebases in seconds.',
    },
    {
        icon: <Wand2 size={28} />,
        title: 'Magic Snippets',
        description: 'Auto-generate snippets from natural language.',
    },
    {
        icon: <MousePointerClick size={28} />,
        title: 'One-Click Docs',
        description: 'Generate full documentation with one tap.',
    },
]

function Button({ className = '', children, ...props }) {
    return (
        <button
            className={`px-5 py-2.5 rounded-xl font-semibold transition backdrop-blur-md bg-white/10 text-white hover:bg-white/20 border border-white/10 shadow-md ${className}`}
            {...props}
        >
            {children}
        </button>
    )
}

export default function App() {
    const [menuOpen, setMenuOpen] = useState(false)
    const [animatedText, setAnimatedText] = useState('')
    const headline = 'The Future of Coding, Now.'

    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 30)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        let index = 0
        const interval = setInterval(() => {
            setAnimatedText(headline.slice(0, index))
            index++
            if (index > headline.length) clearInterval(interval)
        }, 60)
        return () => clearInterval(interval)
    }, [])

    return (
        <div className="bg-gradient-to-br from-black via-gray-900 to-gray-800 min-h-screen text-white font-sans relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,#00f_5%,transparent_30%),radial-gradient(circle_at_80%_80%,#00f2_5%,transparent_40%)] animate-pulse blur-2xl opacity-20 pointer-events-none z-0"></div>
            <header
                className={`fixed top-1 left-1/2 transform -translate-x-1/2 z-50 p-4 md:p-6 flex items-center justify-between rounded-2xl shadow-lg w-[95%] max-w-6xl transition-colors duration-300 ${
                    scrolled
                        ? 'bg-black/80 backdrop-blur-md border border-white/10'
                        : 'bg-white/5 backdrop-blur-xl border border-white/10'
                }`}
            >
                {/* Logo */}
                <a href="/" className="flex-shrink-0">
                    <img src={logo} alt="Glider Logo" className="h-10 w-auto" />
                </a>

                {/* Desktop Nav */}
                <nav className="hidden sm:flex flex-wrap justify-center space-x-6 text-base font-semibold text-white">
                    <a href="#features" className="hover:text-blue-400">
                        Features
                    </a>
                    <a href="#vision" className="hover:text-blue-400">
                        Vision
                    </a>
                    <a href="#about" className="hover:text-blue-400">
                        About
                    </a>
                    <a href="#pricing" className="hover:text-blue-400">
                        Pricing
                    </a>
                    <a href="#contact" className="hover:text-blue-400">
                        Contact
                    </a>
                </nav>

                {/* Desktop Button */}
                <div className="hidden sm:block">
                    <Button className="bg-blue-500 hover:bg-blue-400 text-black shadow-lg shadow-blue-500/30">
                        Get Started
                    </Button>
                </div>

                {/* Hamburger Icon (Mobile) */}
                <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="sm:hidden text-white"
                >
                    {menuOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
            </header>
            {/* Mobile Nav */}
            {menuOpen && (
                <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-40 w-[90%] max-w-xs bg-black/90 backdrop-blur-xl rounded-xl p-6 shadow-xl sm:hidden text-center space-y-4 text-white font-semibold">
                    <a
                        href="#features"
                        className="block hover:text-blue-400"
                        onClick={() => setMenuOpen(false)}
                    >
                        Features
                    </a>
                    <a
                        href="#about"
                        className="block hover:text-blue-400"
                        onClick={() => setMenuOpen(false)}
                    >
                        About
                    </a>
                     <a
                        href="#vision"
                        className="block hover:text-blue-400"
                        onClick={() => setMenuOpen(false)}
                    >
                        Vision
                    </a>
                    <a
                        href="#pricing"
                        className="block hover:text-blue-400"
                        onClick={() => setMenuOpen(false)}
                    >
                        Pricing
                    </a>
                    <a
                        href="#contact"
                        className="block hover:text-blue-400"
                        onClick={() => setMenuOpen(false)}
                    >
                        Contact
                    </a>
                    <Button
                        className="w-full bg-blue-500 hover:bg-blue-400 text-black shadow-lg shadow-blue-500/30"
                        onClick={() => setMenuOpen(false)}
                    >
                        Get Started
                    </Button>
                </div>
            )}
            <div className="h-28" /> {/* Spacer for fixed header */}
            <main className="text-center mt-20 px-4 relative z-10">
                <motion.h2
                    className="text-4xl md:text-6xl font-extrabold text-white"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                >
                    {animatedText}
                    <span className="blinking-cursor">|</span>
                </motion.h2>
                <p className="mt-6 text-lg text-gray-400 max-w-2xl mx-auto">
                    Glider is your intelligent, futuristic coding editor—built
                    with AI at its core to help you code faster, smarter, and
                    cleaner.
                </p>
                <p className="mt-4 text-md text-blue-400 font-medium">
                    Be a coding Jedi, glide through effortlessly, come onboard
                    padavan!
                </p>
                <motion.div
                    className="mt-10 flex justify-center gap-6 flex-wrap"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 }}
                >
                    <Button className="bg-blue-600 hover:bg-blue-500 shadow-blue-400 shadow-md">
                        Start Coding
                    </Button>
                    <Button className="border border-white hover:bg-white hover:text-black">
                        Watch Demo
                    </Button>
                </motion.div>
            </main>
            <section id="features" className="py-24 px-6 relative z-10">
                <h3 className="text-4xl font-bold text-center mb-16 text-white">
                    Features That Feel Like Magic
                </h3>
                <motion.div
                    className="flex flex-col gap-10 max-w-5xl mx-auto"
                    initial="hidden"
                    animate="visible"
                    variants={{
                        visible: {
                            transition: {
                                staggerChildren: 0.1,
                            },
                        },
                    }}
                >
                    {features.map(({ icon, title, description }, i) => (
                        <motion.div
                            key={i}
                            className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 shadow-lg hover:shadow-blue-500/50 transition-shadow w-full"
                            whileHover={{ scale: 1.02 }}
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                visible: { opacity: 1, y: 0 },
                            }}
                        >
                            <div className="flex items-center gap-4">
                                <div className="text-blue-400 animate-pulse">
                                    {icon}
                                </div>
                                <div>
                                    <h4 className="text-xl font-semibold mb-1 text-white">
                                        {title}
                                    </h4>
                                    <p className="text-gray-400">
                                        {description}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </section>
            <section id="vision" className="py-24 px-6 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                    <h3 className="text-3xl font-bold mb-4 text-white">
                        Our Vision
                    </h3>
                    <p className="text-gray-400 mb-4">
                        Glider's vision is to be a powerful coding assistant accessible to everyone, regardless of their coding background. We believe in empowering individuals from various fields to leverage the power of automation.
                    </p>
                    <ul className="text-gray-400 text-left mx-auto max-w-2xl list-disc list-inside space-y-2">
                        <li>It's a coding assistant for non-coders.</li>
                        <li>Be a developer, product designer, product manager, analyst, designer or even a shop owner.</li>
                        <li>Whoever wants an automated way of doing things, Glider brings that.</li>
                    </ul>
                </div>
            </section>
            <section
                id="about"
                className="py-24 px-6 bg-gray-900 relative z-10"
            >
                <div className="max-w-4xl mx-auto text-center">
                    <h3 className="text-3xl font-bold mb-4 text-white">
                        Why Glider?
                    </h3>
                    <p className="text-gray-400">
                        Born from the minds of developers tired of clunky IDEs,
                        Glider is built to be sleek, smart, and seamless. It's
                        your launchpad for creative, efficient coding.
                    </p>
                </div>
            </section>
            <section id="pricing" className="py-24 px-6 relative z-10">
                <h3 className="text-3xl font-bold text-center mb-12 text-white">
                    Simple Pricing
                </h3>
                <div className="flex flex-col md:flex-row gap-10 max-w-4xl mx-auto justify-center">
                    <div className="bg-white/5 p-8 rounded-xl shadow-xl backdrop-blur-md border border-white/10 text-white">
                        <h4 className="text-xl font-bold mb-2">Free</h4>
                        <p className="text-gray-400 mb-4">
                            All basic features and community access.
                        </p>
                        <p className="text-2xl font-bold">$0/mo</p>
                        <Button className="mt-6 bg-blue-600 hover:bg-blue-500">
                            Get Started
                        </Button>
                    </div>
                    <div className="bg-blue-600 p-8 rounded-xl shadow-lg text-black">
                        <h4 className="text-xl font-bold mb-2">Pro</h4>
                        <p className="mb-4">
                            Everything in Free plus advanced AI tools and cloud
                            features.
                        </p>
                        <p className="text-2xl font-bold">$19/mo</p>
                        <Button className="mt-6 bg-black text-white hover:bg-gray-900">
                            Upgrade
                        </Button>
                    </div>
                </div>
            </section>
            <footer
                id="contact"
                className="py-16 bg-black text-center relative z-10"
            >
                <p className="text-gray-400">
                    &copy; 2025 Glider AI. All rights reserved.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                    Built with ❤️ by developers for developers.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                    Contact us at{' '}
                    <a
                        href="mailto:hello@enterglider.com"
                        className="text-blue-400 hover:underline"
                    >
                        hello@enterglider.com
                    </a>
                </p>
            </footer>
            <style jsx>{`
                .blinking-cursor {
                    animation: blink 1s step-end infinite;
                }
                @keyframes blink {
                    50% {
                        opacity: 0;
                    }
                }
            `}</style>
        </div>
    )
}
